import { createContext, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useAlert from "../Alerts/useAlert";

import { defaultLot, Lot } from "../Types/Lot";
import { client } from '../Utilities/AuthUtils';
import useLotDetails from '../Utilities/useLotDetails';

interface LotDetailsContextInterface {
    lotDetail: Lot,
    getLotDetail: (revisionId?: number, fetchQuietly?: boolean) => Promise<void>,
    getLotQualities: (revisionId?: number, fetchQuietly?: boolean) => Promise<void>,
    getLotGenealogy: (revisionId?: number, fetchQuietly?: boolean) => Promise<void>,
    checkForLotChanges: (lotNumber: string, revisionId: number, status: string, processingStatus: string, noOfComments: number, noOfPackages: number, signal?: AbortSignal) => Promise<boolean>,
    updateLotStatus: (status: string, comment: string, reasonCode?: string, message?: string) => Promise<void>,
    loadingLotDetails: boolean,
    loadingQualities: boolean,
    loadingGenealogy: boolean,
    lotFlags: LotFlags,
    onLotRenamed?: (oldLotNumber: string, newLotNumber: string, updatedLot: Lot) => void,
}

interface LotFlags {
    canLotSplit: boolean,
    canFullRecalc: boolean,
    canExportProcessData: boolean,
    canEditLotBuildTime: boolean,
    canAutoSplit: boolean,
    hasRFTCode: boolean,
    hasHeaderText: boolean,
    hasPackoutGrade: boolean,
    hasLotSize: boolean,
    hasExtruder: boolean,
    hasPackageCount: boolean,
    hasMaterialCodes: boolean,
    canPackageQualitySplit: boolean,
    recalcAllQualitiesOnQualitySplit: boolean,
}

const defaultLotFlags: LotFlags = {
    canLotSplit: true,
    canFullRecalc: true,
    canExportProcessData: true,
    canEditLotBuildTime: false,
    canAutoSplit: false,
    hasRFTCode: false,
    hasHeaderText: false,
    hasExtruder: true,
    hasLotSize: true,
    hasPackoutGrade: true,
    hasPackageCount: false,
    hasMaterialCodes: true,
    canPackageQualitySplit: false,
    recalcAllQualitiesOnQualitySplit: true,
};

export const LotDetailContext = createContext<LotDetailsContextInterface>({
    lotDetail: defaultLot,
    getLotDetail: async (revisionId?: number, fetchQuietly: boolean = false) => {},
    getLotQualities: async (revisionId?: number, fetchQuietly: boolean = false) => {},
    getLotGenealogy: async (revisionId?: number, fetchQuietly: boolean = false) => {},
    checkForLotChanges: async (lotNumber: string, revisionId: number, status: string, processingStatus: string, noOfComments: number, noOfPackages: number, signal?: AbortSignal) => false,
    updateLotStatus: async (status: string, comment: string, reasonCode?: string, message?: string) => {},
    loadingLotDetails: false,
    loadingQualities: false,
    loadingGenealogy: false,
    lotFlags: defaultLotFlags,
});

interface LotDetailProviderProps {
    initialLot: Lot,
    onLotRenamed?: (oldLotNumber: string, newLotNumber: string, updatedLot: Lot) => void,
    children: ReactNode,
}

export const LotDetailProvider = ({ initialLot, onLotRenamed, children }: LotDetailProviderProps) => {
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const [lotNumber, setLotNumber] = useState<string>(initialLot?.summary?.lotNumber);
    const [lotFlags, setLotFlags] = useState<LotFlags>(defaultLotFlags);

    const {
        lotDetails,
        getLotDetails,
        getLotQualities,
        getLotGenealogy,
        errorCode,
        loadingLotDetails,
        loadingQualities,
        loadingGenealogy,
        checkForLotChanges,
        updateLotStatus,
        setLotDetails,
    } = useLotDetails(lotNumber!, initialLot);

    useEffect(() => {
        setLotDetails({ ...initialLot });
        setLotNumber(initialLot?.summary?.lotNumber);
    }, [initialLot, setLotDetails]);

    useEffect(() => {
        if (errorCode === 404) {
            setAlert('danger', `Lot ${lotNumber} not found`, 'Redirecting to the home page');
            navigate('/lots', { replace: true });
        } else if (errorCode === 403) {
            const account = client.getAllAccounts()[0];
            setAlert('danger', 'Forbidden', `User: ${account.username} is not authorized to view lot ${lotNumber}`);
            navigate('/lots', { replace: true });
        } else if (errorCode === 401) {
            const account = client.getAllAccounts()[0];
            setAlert('danger', 'Unauthorized', `User: ${account.username} is not authorized to view lot ${lotNumber}`);
            navigate('/lots', { replace: true });
        }
    }, [errorCode, lotNumber, navigate, setAlert]);

    const getLotFlags = (businessUnitCode: string | undefined): LotFlags => {
        switch (businessUnitCode) {
            case "BAPP_PP":
                return {
                    ...defaultLotFlags,
                    canLotSplit: false,
                    hasMaterialCodes: false
                };
            case "BTBU":
                return {
                    ...defaultLotFlags,
                    canFullRecalc: false,
                    canExportProcessData: false,
                    canEditLotBuildTime: true,
                    hasExtruder: false,
                    canPackageQualitySplit: true,
                    recalcAllQualitiesOnQualitySplit: true
                };
            case "BRPO_HDPE":
            case "BRPO_PP1":
            case "BRPO_PP2":
                return {
                    ...defaultLotFlags,
                    canLotSplit: false,
                    hasRFTCode: true,
                    hasMaterialCodes: false
                };
            case "BRCP_HFU":
            case "BRCP_VFU":
                return {
                    ...defaultLotFlags,
                    canFullRecalc: false,
                    canExportProcessData: false,
                    canEditLotBuildTime: true,
                    canAutoSplit: true,
                    hasHeaderText: true,
                    hasExtruder: false,
                    hasLotSize: false,
                    hasPackageCount: true,
                    canPackageQualitySplit: true,
                    recalcAllQualitiesOnQualitySplit: false
                };
            case "HCC_PP":
            case "HCC_GPPE":
            case "HCC_LDPE":
            case "SCP_PP":
            case "SCP_PE":
            case "SCP_SME":
            default:
                return defaultLotFlags;
        }
    };

    useEffect(() => {
        setLotFlags(getLotFlags(lotDetails?.summary?.businessUnitCode));
    }, [lotDetails?.summary?.businessUnitCode]);

    return (
        <LotDetailContext.Provider value={{
            lotDetail: lotDetails ?? defaultLot,
            getLotDetail: getLotDetails,
            getLotQualities,
            getLotGenealogy,
            checkForLotChanges,
            updateLotStatus,
            loadingLotDetails,
            loadingQualities,
            loadingGenealogy,
            lotFlags,
            onLotRenamed
        }}>
            {children}
        </LotDetailContext.Provider>
    );
};

